import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { SortConfig, TableColumn } from "@shared/types/table";
import React, { useState } from "react";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useConsumerDocuments } from "../../../hooks";

interface DocumentsTabProps {
  consumerId?: string;
  consumerNo?: string;
  remoteUtilityId?: number;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
}) => {
  const { remoteUtilityId: loginUtilityId, consumerId: loginConsumerId } = getLoginDataFromStorage();
  const effectiveUtilityId = remoteUtilityId || Number(loginUtilityId);
  const effectiveConsumerId = consumerId || loginConsumerId;
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: documentHookData, isLoading } = useConsumerDocuments(effectiveConsumerId, {
    remote_utility_id: effectiveUtilityId,
    page: currentPage,
    limit: 10,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    query: searchTerm,
  });

  // Define table columns
  const columns: TableColumn<any>[] = [
    {
      key: "documentType",
      header: "Type",
    },
    {
      key: "documentSubtype",
      header: "Subtype"
    },
    {
      key: "createdDate",
      header: "Upload Date",
      render: (value) => {
        return value ? new Date(value).toLocaleDateString() : "-";
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Verified":
              return "default";
            case "Pending":
              return "secondary";
            case "Rejected":
              return "outline";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Verified":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "Pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            case "Rejected":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "createdUserRemoteName",
      header: "Uploaded By",
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Updated By",
    },
  ];

  // Table actions for each row
  const actions = [
    {
      label: "View",
      icon: "Eye" as const,
      onClick: (row: any, index: number) => {
        if (!row.file) return;
        const link = document.createElement("a");
        link.href = row.file;
        link.target = "_blank";
        link.download = row.file.split("/").pop() || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      variant: "ghost" as const,
    },
  ];

  // Handle sorting
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Account Documents"
          subtitle="View account documents, attached contracts, and upload additional files."
        />
      </div>
      {/* Generic Table */}
      <GenericTable
        data={documentHookData?.results || documentHookData?.result?.results || documentHookData?.result || []}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        pagination={{
          currentPage: documentHookData?.page || documentHookData?.result?.page || currentPage,
          totalPages: documentHookData?.totalPages || documentHookData?.result?.totalPages || 1,
          totalItems: documentHookData?.total || documentHookData?.result?.total || 0,
          itemsPerPage: documentHookData?.limit || documentHookData?.result?.limit || 10,
          startItem: ((documentHookData?.page || documentHookData?.result?.page || 1) - 1) * (documentHookData?.limit || documentHookData?.result?.limit || 10) + 1,
          endItem: Math.min(
            (documentHookData?.page || documentHookData?.result?.page || 1) * (documentHookData?.limit || documentHookData?.result?.limit || 10),
            documentHookData?.total || documentHookData?.result?.total || 0
          ),
        }}
        onPageChange={handlePageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchPlaceholder="Search documents..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No documents found"
      />
    </div>
  );
};
